import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, finalize, Observable, take } from 'rxjs';
import { AlertToastService } from '@services';
import {
  Unsubscriber,
  PartialSuccessService,
  hasFailedEntities,
  PartialSuccessResponse,
  CourierPickupInsertInvoicesPartialSuccessConfig,
  BannerType,
} from '@shared';
import { CourierPickupCompact } from '../../../../components/dashboard/business-services/courier-pickup/courier-pickup.model';
import { CourierPickupApiService } from '../../../../components/dashboard/business-services/services/courier-pickup-api/courier-pickup-api.service';
import { NewCourierPickup } from './courier-pickups-list.constants';

@Component({
  selector: 'app-courier-pickups-list',
  templateUrl: './courier-pickups-list.component.html',
  styleUrls: ['./courier-pickups-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CourierPickupsListComponent extends Unsubscriber implements OnInit {
  protected readonly newCourierPickup = NewCourierPickup;
  protected readonly BannerType = BannerType;

  constructor(
    private courierApiService: CourierPickupApiService,
    private dialog: MatDialogRef<CourierPickupsListComponent>,
    private alert: AlertToastService,
    private partialSuccess: PartialSuccessService,
    @Inject(MAT_DIALOG_DATA) private data: { invoiceIds: string[] },
  ) {
    super();
  }

  selectedPickup: string = NewCourierPickup;

  private _pickups: BehaviorSubject<CourierPickupCompact[]> = new BehaviorSubject<CourierPickupCompact[]>([]);
  pickups$: Observable<CourierPickupCompact[]> = this._pickups.asObservable();

  private _loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  loading$: Observable<boolean> = this._loading.asObservable();

  set loading(value: boolean) {
    this._loading.next(value);
  }

  ngOnInit(): void {
    this.loadPickups();
  }

  close(): void {
    this.dialog.close(null);
  }

  save(): void {
    const number: string = this.selectedPickup === NewCourierPickup ? '' : this.selectedPickup;

    if (!!number) {
      this.attachToSelected(number);
    } else {
      this.dialog.close({ success: false, partialSuccess: false, Barcode: number });
    }
  }

  private attachToSelected(number: string): void {
    this.loading = true;

    this.subscriptions = this.courierApiService.insertDocuments(number, this.data.invoiceIds)
      .pipe(
        finalize(() => this.loading = false),
        take(1),
      )
      .subscribe((res: PartialSuccessResponse) => {
          if (res) {
            if (hasFailedEntities(res)) {
              const config = {
                ...CourierPickupInsertInvoicesPartialSuccessConfig,
                label: res.Barcode ? `№${res.Barcode}` : ``,
              };
              this.partialSuccess.open(res, config);
              this.dialog.close({ success: true, partialSuccess: true, Barcode: number });
            } else {
              this.dialog.close({ success: true, partialSuccess: false, Barcode: number });
              this.alert.pushSuccess('Посилка(и) успішно додано');
            }
          } else {
            this.dialog.close({ success: false, partialSuccess: false, Barcode: number });
          }
        },
        (err) => {
          if (err[0]) {
            this.loading = false;
            this.dialog.close({ success: false, partialSuccess: false, Barcode: number });
            this.alert.pushApiErrors(err);
          }
        });
  }

  get saveBtnLabel(): string {
    return this.selectedPickup === NewCourierPickup ? 'Створити заявку' : 'додати до заявки';
  }

  private loadPickups(): void {
    this.loading = true;

    const invoiceIdForSearch = this.data.invoiceIds.length === 1 ? this.data.invoiceIds[0] : undefined;

    this.subscriptions = this.courierApiService.getCourierPickupCompactList(invoiceIdForSearch)
      .pipe(finalize(() => this.loading = false))
      .subscribe(
        (res: CourierPickupCompact[]) => {
          if (res) {
            this._pickups.next(res);
          }
        },
        (err) => {
          if (err[0]) {
            this.alert.pushApiErrors(err);
          }
        });
  }
}
